import React, { useEffect, useMemo, useState } from 'react';
import { UserGroupIcon, UserIcon } from '@heroicons/react/16/solid';
import { Dialog } from '../common/Catalyst/dialog';
import { Button } from '../common/Catalyst/button';
import { HoaReturn, MemberReturn } from '../clients/rust-api-client';
import { memberService, notificationService } from '../clients/ApiClients';
import { apiErrorMessage } from '../utils/apiErrors';
import { ErrorAlert } from '../common/Custom/ErrorAlert';
import { useSenderName } from '../hooks/UserContext';
import { PropertyPicker } from '../violation/linear/PropertyPicker';
import { CreateScope } from '../shell/createScope';
import { ScopeChips, useForHoa, useScopeState } from '../shell/ScopeChips';
import { useSyncedMembers } from '../sync/hooks';
import {
  ALL_MEMBERS,
  SUBJECT_MAX,
  buildMessageRequest,
  missingForMessage,
} from './communicationMeta';

/**
 * Send a message to homeowners (#632), in the Linear create shape: the
 * subject as the large title, the message as the free-form body, and
 * association and recipient as chips. Replaces SendNotificationDialog.
 *
 * This reaches real people's inboxes, so nothing about it is implicit:
 * the footer says who it goes to and whether it is emailed, the button
 * repeats the recipient, and no palette command or shortcut sends — they
 * only open this.
 */
interface Props {
  open: boolean;
  hoas: HoaReturn[];
  hoasLoading?: boolean;
  /** `memberId` picks that member as the recipient. */
  scope?: CreateScope;
  /** The page fixed the scope it passed; those chips are not changeable. */
  lockScope?: boolean;
  onClose: () => void;
  onSent: () => void;
}

const activeByName = (members: MemberReturn[]): MemberReturn[] =>
  members
    .filter((m) => m.is_active !== false)
    .sort((a, b) => memberName(a).localeCompare(memberName(b)));

const fetchMembers = async (hoaId: string): Promise<MemberReturn[]> =>
  activeByName((await memberService.getMembersByHoa({ hoaId })).data);

const memberName = (m: MemberReturn): string =>
  m.full_name || m.display_name || m.email;

export const SendMessageModal: React.FC<Props> = ({
  open,
  hoas,
  hoasLoading,
  scope = {},
  lockScope,
  onClose,
  onSent,
}) => {
  const senderName = useSenderName();
  const { hoaId, setHoaId } = useScopeState(open, scope, hoas);
  const [recipient, setRecipient] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set after a send that reached some members and not others: sending
  // again would message the ones it reached twice.
  const [partlySent, setPartlySent] = useState(false);

  // The sync engine's copy when it has one (#561); fetched otherwise.
  const syncedMembers = useSyncedMembers({ hoaId });
  const members = useForHoa(
    hoaId,
    fetchMembers,
    'members',
    useMemo(
      () => (hoaId && syncedMembers ? activeByName(syncedMembers) : null),
      [hoaId, syncedMembers]
    )
  );

  useEffect(() => {
    if (!open) return;
    setRecipient(scope.memberId || ALL_MEMBERS);
    setSubject('');
    setBody('');
    setSendEmail(true);
    setError(null);
    setPartlySent(false);
    // Only on open: the scope object is rebuilt on every render upstream.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const changeHoa = (id: string) => {
    setHoaId(id);
    // A member belongs to one association, so start again from everyone.
    setRecipient(ALL_MEMBERS);
  };

  const hoa = hoas.find((h) => h.id === hoaId);
  const member = members.items.find((m) => m.id === recipient);
  const toAll = recipient === ALL_MEMBERS;
  const draft = { hoaId, recipient, subject, body, senderName, sendEmail };
  const missing = missingForMessage(draft);
  const request = buildMessageRequest(draft);
  // Known to have nobody to send to — not merely still loading.
  const nobodyToSendTo =
    toAll && !members.loading && !members.error && members.items.length === 0;
  const canSend = !!request && !sending && !partlySent && !nobodyToSendTo;

  const audience = (): string => {
    if (!hoa) return '';
    if (toAll) {
      if (members.loading || members.error)
        return `every member of ${hoa.name}`;
      const n = members.items.length;
      return n === 1
        ? `the one member of ${hoa.name}`
        : `all ${n} members of ${hoa.name}`;
    }
    return member ? `${memberName(member)} (${member.email})` : '';
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!request || sending) return;
    setSending(true);
    setError(null);
    try {
      if (request.kind === 'hoa') {
        const { data } = await notificationService.sendHoaMessage({
          hoaMessageRequest: request.request,
        });
        onSent();
        if (data.failed_count > 0) {
          // Some went: say so rather than invite a resend to everyone.
          setPartlySent(true);
          setError(
            `Sent to ${data.successful_count} of ${data.total_requested}. ` +
              `${data.failed_count} could not be sent — message them one at a time.`
          );
          return;
        }
      } else {
        await notificationService.sendMemberMessage({
          memberMessageRequest: request.request,
        });
        onSent();
      }
      onClose();
    } catch (err) {
      setError(
        apiErrorMessage(err, 'The message was not sent. Please try again.')
      );
    } finally {
      setSending(false);
    }
  };

  const who = audience();

  return (
    <Dialog open={open} onClose={() => !sending && onClose()} size="2xl">
      <form onSubmit={submit} aria-label="Send message">
        <p className="text-[12px] font-medium text-fg-muted">Send message</p>

        <input
          autoFocus
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={SUBJECT_MAX}
          placeholder="Subject"
          aria-label="Subject"
          className="mt-2 w-full border-0 bg-transparent p-0 text-xl font-semibold text-fg placeholder:text-fg-faint focus:outline-none focus:ring-0"
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={7}
          placeholder="Write the message the homeowners will read."
          aria-label="Message"
          className="mt-3 w-full resize-none border-0 bg-transparent p-0 text-[15px]/6 text-fg-secondary placeholder:text-fg-faint focus:outline-none focus:ring-0"
        />

        <div className="mt-2 flex flex-wrap items-center gap-1 border-t border-line/60 pt-3">
          <ScopeChips
            hoas={hoas}
            hoasLoading={hoasLoading}
            hoaId={hoaId}
            onHoaChange={changeHoa}
            lockHoa={lockScope && !!scope.hoaId}
          />
          <PropertyPicker
            options={[
              {
                value: ALL_MEMBERS,
                label: 'All members',
                icon: (
                  <UserGroupIcon
                    className="h-3.5 w-3.5 text-fg-muted"
                    aria-hidden
                  />
                ),
              },
              ...members.items.map((m) => ({
                value: m.id,
                label: `${memberName(m)} · ${m.email}`,
                icon: (
                  <UserIcon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
                ),
              })),
            ]}
            value={recipient}
            onChange={setRecipient}
            placeholder="Member…"
            ariaLabel={
              toAll
                ? 'To: all members'
                : member
                  ? `To: ${memberName(member)}`
                  : 'Choose recipient'
            }
            readOnly={
              (lockScope && !!scope.memberId) || !hoaId || members.loading
            }
            trigger={
              <>
                {toAll ? (
                  <UserGroupIcon
                    className="h-3.5 w-3.5 text-fg-muted"
                    aria-hidden
                  />
                ) : (
                  <UserIcon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
                )}
                {toAll ? (
                  'All members'
                ) : member ? (
                  memberName(member)
                ) : (
                  <span className="text-fg-muted">
                    {members.loading ? 'Loading members…' : 'Recipient'}
                  </span>
                )}
              </>
            }
          />
        </div>
        <ErrorAlert className="mt-2">{members.error}</ErrorAlert>

        <ErrorAlert className="mt-3">{error}</ErrorAlert>

        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3 border-t border-line/60 pt-4">
          <label className="flex items-center gap-2 text-[13px] text-fg-secondary">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="h-4 w-4 rounded border-line-strong bg-transparent text-accent focus:ring-accent"
            />
            Email it too
          </label>
          <p
            className="min-w-0 flex-1 text-[12px] text-fg-muted"
            aria-live="polite"
          >
            {nobodyToSendTo && hoa
              ? `${hoa.name} has no members to send to.`
              : who
                ? `Sends to ${who} · ${sendEmail ? 'in the portal and by email' : 'in the portal only'}`
                : missing.length > 0 && (subject || body)
                  ? `Still needs ${missing.join(', ')}.`
                  : ''}
          </p>
          <div className="ml-auto flex items-center gap-2">
            {partlySent ? (
              <Button color="sky" type="button" onClick={onClose}>
                Close
              </Button>
            ) : (
              <>
                <Button
                  plain
                  type="button"
                  onClick={onClose}
                  disabled={sending}
                >
                  Cancel
                </Button>
                <Button
                  color="sky"
                  type="submit"
                  disabled={!canSend}
                  title={
                    missing.length ? `Add ${missing.join(', ')}` : undefined
                  }
                >
                  {sending ? 'Sending…' : 'Send'}
                </Button>
              </>
            )}
          </div>
        </div>
      </form>
    </Dialog>
  );
};
